import { Card, CardBody, Row, Col } from 'reactstrap';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import CustomerDetailsSection from './CustomerDetailsSection';
import ProductsSection from './ProductsSection';
import DeliveryDetailsSection from './DeliveryDetailsSection';
import BookingQuestionsSection, { BookingQuestion } from './BookingQuestionsSection';
import BookingStatusSection from './BookingStatusSection';
import OrderSummarySection from './OrderSummarySection';
import PaymentSection from './Payment';
import Spinner from 'Components/UI/Spinner';
import { getPublicAdminSettings } from 'helpers/API Calls/System & Settings/Admin Settings';
import { getPostcodes } from 'helpers/API Calls/System & Settings/Postcodes';
import { getBookingStatus } from 'helpers/API Calls/Operations/booking';
import { SettingItem } from 'types/types';
import { Postcode } from './types';
import { selectIsEdit, setStripeConfig, setTaxesAndDeposits } from 'slices/booking/cartSlice';
import { setPostcodeDetails, setBookingStatuses } from 'slices/booking/bookingSlice';
import RentalAgreement from './RentalAgreement';
import { useSelector } from 'react-redux';
import { getBookingQuestions } from 'helpers/API Calls/System & Settings/Booking Questions';

function BookingForm() {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState<boolean>(true);
  const isEdit = useSelector(selectIsEdit);
  const [bookingQuestions, setBookingQuestions] = useState<BookingQuestion[]>([]);
  

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch admin settings
        if(!isEdit){
          const charges = await getPublicAdminSettings('key=VAT_VALUE,REFUNDABLE_CLEANING_DEPOSIT,STRIPE_PAYMENT,STRIPE_PUBLIC_KEY,STRIPE_SECRET,DEPOSIT_AMOUNT,DEPOSIT');
          if(charges?.success) {
            const vat = charges.data.find((item: SettingItem) => item.key === 'VAT_VALUE')?.value;
            const cleaningDeposit = charges.data.find((item: SettingItem) => item.key === 'REFUNDABLE_CLEANING_DEPOSIT')?.value;
            const stripePublicKey = charges.data.find((item: SettingItem) => item.key === 'STRIPE_PUBLIC_KEY')?.value;
            const isStripeEnabled = charges.data.find((item: SettingItem) => item.key === 'STRIPE_PAYMENT')?.value === 'ENABLED';
            const depositAmount = charges.data.find((item: SettingItem) => item.key === 'DEPOSIT_AMOUNT')?.value;
            const isDepositEnabled = charges.data.find((item: SettingItem) => item.key === 'DEPOSIT')?.value === 'ENABLED';

            // Dispatch stripe configuration to cart slice
            dispatch(setStripeConfig({
              publicKey: stripePublicKey,
              isEnabled: isStripeEnabled
            }));

            // console.log({vat, cleaningDeposit, depositAmount, isDepositEnabled});

            // Dispatch VAT and cleaning deposit
            dispatch(setTaxesAndDeposits({
              vat: Number(vat) || 0,
              cleaningDeposit: Number(cleaningDeposit) || 0, 
              deposit: Number(depositAmount) || 0,
              isDepositEnabled: isDepositEnabled
            }));
          }
        }

        // Fetch and dispatch postcodes
        const codes = await getPostcodes('limit=10000&status=active');
        if (codes.success) {
          const formattedCodes = codes.data.items.map((code: Postcode) => ({
            id: code.id,
            postcode: code.postcode,
            town: code.town,
            region: code.region,
            country: code.country,
            country_string: code.country_string,
            charges: code.charges
          }));
          dispatch(setPostcodeDetails(formattedCodes));
        }

        // Fetch and dispatch booking statuses
        const bookingStatuses = await getBookingStatus('limit=10000&status=active');
        if(bookingStatuses.success) {
          const formattedStatuses = bookingStatuses.data.map((status: any) => ({
            id: status.id,
            name: status.status_name,
            slug: status.status_slug,
            type: status.status_type,
          }));
          dispatch(setBookingStatuses(formattedStatuses));
        }

        const response = await getBookingQuestions('limit=100000&status=active');
        if (response.success) {
          setBookingQuestions(response.data.items);
        }
      } catch (error) {
        console.error('Error fetching booking form data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [dispatch, isEdit]);

  return (
    <>
      {loading ? (
        <div className="position-relative vh-100"><Spinner/></div>
      ) : (
        <Row>
          <Col xl={8}>
            <Card className="mb-4">
              <CardBody>
                <h4 className="card-title mb-4">Products</h4>
                <ProductsSection/>
              </CardBody>
            </Card>


            <Card className="mb-4">
              <CardBody>
                <h4 className="card-title mb-4">Customer Details</h4>
                <CustomerDetailsSection/>
              </CardBody>
            </Card>

            <Card className="mb-4">
              <CardBody>
                <h4 className="card-title mb-4">Delivery Details</h4>
                <DeliveryDetailsSection />
              </CardBody>
            </Card>

            {bookingQuestions.length > 0 && (
              <Card className="mb-4">
                <CardBody>
                  <h4 className="card-title mb-4">Booking Questions</h4>
                  <BookingQuestionsSection bookingQuestions={bookingQuestions} />
                </CardBody>
              </Card>
            )}


          </Col>
          
          {/* Right Column - Status and Summary */}
          <Col xl={4}>
            <Card>
              <CardBody>
                <h4 className="card-title mb-4">Order Summary</h4>
                <OrderSummarySection/>
              </CardBody>
            </Card>

            <Card className="mb-4">
              <CardBody>
                <h4 className="card-title mb-4">Booking Information</h4>
                <BookingStatusSection/>
              </CardBody>
            </Card>

            <Card className="mb-4">
              <CardBody>
                <h4 className="card-title mb-4">Payment</h4>
                <PaymentSection/>
              </CardBody>
            </Card>

            {isEdit &&
            <Card className="mb-4">
              <CardBody>
                <h4 className="card-title mb-4">Rental Agreement</h4>
                <RentalAgreement/>
              </CardBody>
            </Card>}

          </Col>
        </Row>
      )}
    </>
  );
}

export default BookingForm;

